/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import _toNumber from "lodash/toNumber";
import _slice from "lodash/slice";
import _cloneDeep from "lodash/cloneDeep";
import { Box, MenuItem } from "@mui/material";
import { useForm } from "@/src/provider/Form/FormProvider";
import Group from "@/src/components/Wizard/Group";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { requiredValidator } from "@/src/services/validationRules";
import Valuable from "./Valuable/Valuable";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";

type CashValuables = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function CashValuables({ submitProps, product }: CashValuables) {
  const { values, handleChange, handleSubmit } = useForm();
  useEffect(() => {
    const numberofValuables = values.numberofValuables ? _toNumber(values.numberofValuables) : 0;
    if (values.valuables.length === numberofValuables) {
      return;
    }
    const emptyValuable = {
      name: "",
      ownership: "",
      value: "",
    };
    let valuables = _cloneDeep(values.valuables);
    for (let i = valuables.length; i < Math.min(numberofValuables, 5); i++) {
      valuables.push(_cloneDeep(emptyValuable));
    }
    valuables = _slice(valuables, 0, numberofValuables);
    handleChange({
      target: {
        type: "other",
        name: "valuables",
        value: valuables,
      },
    });
  }, [values.valuables, values.numberofValuables]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <FormLegend>Wie viel Bargeld besitzen Sie?</FormLegend>
            </Grid>
            <NumberFieldConfigured caseData={product} fieldPath={"cashAmount"} fieldType={"money"} numberType={"float"} required />

            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasValuables"}
                question={"Besitzen Sie Wertgegenstände?"}
                modalContent={<p>Hierzu zählen z.B. wertvoller Schmuck, Antiquitäten oder hochwertige elektronische Geräte.</p>}
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasValuables === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele Wertgegenstände besitzen Sie?"
                  name="numberofValuables"
                  validators={[requiredValidator("Bitte geben Sie an, wie viele Wertgegenstände Sie besitzen")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                  <MenuItem value={"3"}>3</MenuItem>
                  <MenuItem value={"4"}>4</MenuItem>
                  <MenuItem value={"5"}>5</MenuItem>
                </ValidatorSelectField>
                {values.valuables?.map((vehicle: any, index: number) => (
                  <Valuable key={index} index={index} path={`valuables[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
